/* eslint-disable */
import React, { useCallback, useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useStore } from '../../store/store';
import { useNav } from '../../hooks/nav';
import Picker from 'react-mobile-picker';
import { ReactComponent as BackIcon } from '../../assets/images/arrowLeft.svg';
import Analytics from '@web-solutions/module-analytics';
import { t } from '@web-solutions/module-localization';
import Button from 'src/onboarding/components/ui/button/Button';
import { Title } from 'src/onboarding/components/typography/Title/Title';
import { Text } from 'src/onboarding/components/typography/Text/Text';
import { Caption } from 'src/onboarding/components/typography/Caption/Caption';
import { calculateAge } from 'src/onboarding/helpers/calculateAge';

const today = new Date();
const currentYear = today.getFullYear();

function getYearsArray() {
  return Array.from({ length: 100 }, (_, i) => (currentYear - i).toString());
}

function getMonthsArray(year) {
  return Array.from({ length: 12 }, (_, i) => {
    const month = i + 1;
    return String(month).padStart(2, '0');
  }).filter(Boolean);
}

function getDayArray(year, month) {
  const dayCount = new Date(year, month, 0).getDate();
  return Array.from({ length: dayCount }, (_, i) => {
    const day = i + 1;
    return String(day).padStart(2, '0');
  }).filter(Boolean);
}

function Page() {
  const birthDate = useStore((state) => state.birthDate);
  const setBirthDate = useStore((state) => state.setBirthDate);

  const defaultDate = {
    year: String(1985),
    month: String(6).padStart(2, '0'),
    day: String(1).padStart(2, '0'),
  };

  const [formattedDate, setFormattedDate] = useState(format(new Date(), 'dd MMM yyyy'));
  const [pickerValue, setPickerValue] = useState(birthDate || defaultDate);
  const { onNext, onPrev, currentIndex } = useNav();

  const [errorMessage, setErrorMessage] = useState('');
  const [isDisabled, setIsDisabled] = useState(false);

  useEffect(() => {
    if (!pickerValue?.year || !pickerValue?.month || !pickerValue?.day) return;

    const age = calculateAge(pickerValue);

    if (age < 18) {
      setErrorMessage(t('onboarding.birthDate.errorMessage'));
      setIsDisabled(true);
    } else {
      setErrorMessage('');
      setIsDisabled(false);
    }
  }, [pickerValue]);

  useEffect(() => {
    const handleTouchMove = (event: TouchEvent) => {
      if (document.querySelector('.data-picker_new')?.contains(event.target as Node)) {
        event.preventDefault();
      }
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  const monthsArray = [
    t('onboarding.common.calendar.January'),
    t('onboarding.common.calendar.February'),
    t('onboarding.common.calendar.March'),
    t('onboarding.common.calendar.April'),
    t('onboarding.common.calendar.May'),
    t('onboarding.common.calendar.June'),
    t('onboarding.common.calendar.July'),
    t('onboarding.common.calendar.August'),
    t('onboarding.common.calendar.September'),
    t('onboarding.common.calendar.October'),
    t('onboarding.common.calendar.November'),
    t('onboarding.common.calendar.December'),
  ];

  const handlePickerChange = useCallback(
    (newValue) => {
      let { year, month, day } = newValue;
      year = Number(year);
      month = Number(month);
      day = Number(day);

      const selectedDate = new Date(year, month - 1, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const age = calculateAge({ year, month, day });

      if (age < 18) {
        setErrorMessage(t('onboarding.birthDate.errorMessage'));
        setIsDisabled(true);
      } else if (age > 99) {
        setIsDisabled(true);
      } else {
        setErrorMessage('');
        setIsDisabled(false);
      }

      setPickerValue({
        year: String(year),
        month: String(month).padStart(2, '0'),
        day: String(day).padStart(2, '0'),
      });

      setBirthDate({
        year: String(year),
        month: String(month).padStart(2, '0'),
        day: String(day).padStart(2, '0'),
      });

      setFormattedDate(format(selectedDate, 'dd MMM yyyy'));
    },
    [setBirthDate, t]
  );

  const handleContinue = useCallback(() => {
    if (isDisabled) return;

    setBirthDate(pickerValue);

    Analytics.trackEvent(`step${currentIndex + 1}`, 'complete', {
      page: 'birth-date',
      answer: pickerValue,
    });

    onNext();
  }, [isDisabled, pickerValue, setBirthDate, currentIndex, onNext]);
  return (
    <div className={'page'}>
      {currentIndex !== 0 && (
        <div className={'back'} onClick={onPrev}>
          <BackIcon />
        </div>
      )}
      <div className={'wrapper-info'}>
        <Title tag='h2'>{t('onboarding.birthDate.title')}</Title>
        <Text size='m' medium className={'description'} style={{ padding: 0 }}>
          {t('onboarding.birthDate.subTitle')}
        </Text>
      </div>
      <div className={'picker-wrapper'}>
        <Picker
          value={pickerValue}
          onChange={handlePickerChange}
          wheelMode='natural'
          height={150}
          className={'data-picker_new'}
          itemHeight={40}
        >
          <Picker.Column name='month' className='col'>
            {getMonthsArray(Number(pickerValue.year)).map((month, index) => (
              <Picker.Item key={month} value={month} className='month-item'>
                {({ selected }) => (
                  <div className={selected ? 'item_selected' : 'item_neutral'}>
                    {monthsArray[Number(month) - 1]}
                  </div>
                )}
              </Picker.Item>
            ))}
          </Picker.Column>

          <Picker.Column name='day' className='col'>
            {getDayArray(Number(pickerValue.year), Number(pickerValue.month)).map((day) => (
              <Picker.Item key={day} value={day} className='item'>
                {({ selected }) => (
                  <div className={selected ? 'item_selected' : 'item_neutral'}>{day}</div>
                )}
              </Picker.Item>
            ))}
          </Picker.Column>

          <Picker.Column name='year' className='col'>
            {getYearsArray().map((year) => (
              <Picker.Item key={year} value={year} className='item'>
                {({ selected }) => (
                  <div className={selected ? 'item_selected' : 'item_neutral'}>{year}</div>
                )}
              </Picker.Item>
            ))}
          </Picker.Column>
        </Picker>
        {errorMessage && (
          <Caption size='m' className={'error-message'}>
            {errorMessage}
          </Caption>
        )}
      </div>

      <div className={'next-wrapper'}>
        <Button
          className={isDisabled ? 'disabled' : ''}
          title={t('onboarding.birthDate.continue')}
          onClick={handleContinue}
        />
      </div>
    </div>
  );
}

export default Page;
